<div class="upload-wizard">
  <div class="upload-wizard__header">
    <span class="upload-wizard__title">Upload Alert Roster</span>
    <span class="upload-wizard__step">{{ stepLabel() }}</span>
  </div>

  <mat-dialog-content class="upload-wizard__content">
    @if (step() === 0) {
      <p class="upload-wizard__lead">Choose a roster file (.csv, .xls, or .xlsx).</p>
      <div class="upload-wizard__file-drop">
        <input
          #fileInput
          type="file"
          class="upload-wizard__file-input"
          accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          (change)="onFileSelected($event)"
        />
        <button mat-stroked-button color="primary" type="button" (click)="fileInput.click()">
          Choose file
        </button>
        @if (parsedFile(); as file) {
          <p class="upload-wizard__meta">{{ file.fileName }} · {{ file.rows.length }} rows</p>
        } @else {
          <p class="upload-wizard__meta">No file chosen</p>
        }
      </div>
    }

    @if (step() === 1) {
      @if (parsedFile(); as file) {
        <p class="upload-wizard__meta">File: {{ file.fileName }}</p>
      }
      <p class="upload-wizard__lead">
        Choose which spreadsheet columns hold the alert subject (name or ID) and the contact phone
        number. Use Add column if the subject is split across more than one column.
      </p>
      <h3 class="upload-wizard__subsection">Alert subject</h3>
      @for (col of columnMapping().subjectColumns; track $index) {
        <div class="upload-wizard__map-row">
          <mat-form-field appearance="fill" floatLabel="always" class="upload-wizard__map-field">
            <mat-select
              [value]="col || null"
              placeholder="Choose column"
              (valueChange)="updateSubjectColumn($index, $event)"
            >
              @for (h of headers(); track h) {
                <mat-option [value]="h">{{ h }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-icon-button type="button" aria-label="Remove column" (click)="removeSubjectColumn($index)">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <button mat-button color="primary" type="button" (click)="addSubjectColumn()">
        <mat-icon>add</mat-icon>
        Add column
      </button>

      <h3 class="upload-wizard__subsection">Contact number</h3>
      @for (col of columnMapping().phoneColumns; track $index) {
        <div class="upload-wizard__map-row">
          <mat-form-field appearance="fill" floatLabel="always" class="upload-wizard__map-field">
            <mat-select
              [value]="col || null"
              placeholder="Choose column"
              (valueChange)="updatePhoneColumn($index, $event)"
            >
              @for (h of headers(); track h) {
                <mat-option [value]="h">{{ h }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-icon-button type="button" aria-label="Remove column" (click)="removePhoneColumn($index)">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <button mat-button color="primary" type="button" (click)="addPhoneColumn()">
        <mat-icon>add</mat-icon>
        Add column
      </button>

      @if (samplePreviewRow(); as sample) {
        <h3 class="upload-wizard__subsection">Preview (row 1)</h3>
        <table class="upload-wizard__preview-table">
          <thead>
            <tr>
              <th>Alert subject</th>
              <th>Contact number</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ sample.subject }}</td>
              <td>{{ sample.phone }}</td>
            </tr>
          </tbody>
        </table>
      }
    }

    @if (step() === 2) {
      @let summary = previewSummary();
      <p class="upload-wizard__meta">
        {{ summary.total }} rows · {{ summary.ready }} ready · {{ summary.attention }} need attention
      </p>
      <div class="upload-wizard__table-wrap">
        <table class="upload-wizard__preview-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Alert subject</th>
              <th>Contact number</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (row of previewRows(); track row.rowIndex) {
              <tr [class.upload-wizard__row--skipped]="row.skipped">
                <td>{{ row.rowIndex + 1 }}</td>
                <td>{{ row.subject }}</td>
                <td>{{ row.phone }}</td>
                <td>{{ row.skipped ? 'Skipped' : row.status }}</td>
                <td>
                  @if (row.status !== 'ready') {
                    <button mat-button color="primary" type="button" (click)="toggleSkip(row)">
                      {{ row.skipped ? 'Include' : 'Skip' }}
                    </button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    @if (step() === 3) {
      <div [formGroup]="settingsForm">
        <mat-form-field appearance="fill" floatLabel="always" class="upload-wizard__full">
          <mat-label>Campaign title</mat-label>
          <input matInput formControlName="title" required />
        </mat-form-field>
        <mat-form-field appearance="fill" floatLabel="always" class="upload-wizard__full">
          <mat-label>Description</mat-label>
          <textarea matInput rows="2" formControlName="description"></textarea>
        </mat-form-field>
        <mat-form-field appearance="fill" floatLabel="always" class="upload-wizard__full">
          <mat-label>Callback location</mat-label>
          <mat-select formControlName="locationId">
            @for (loc of locations(); track loc.id) {
              <mat-option [value]="loc.id">{{ loc.name }} — {{ loc.phone }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <h3 class="upload-wizard__subsection">Alert configuration</h3>
        <app-alert-config-form-fields
          [form]="alertConfigForm"
          [showCampaignSchedule]="true"
          [cycleSlots]="customSlots()"
          [customScheduleControl]="settingsForm.controls.customScheduleEnabled"
        />
      </div>
    }

    @if (step() === 4) {
      <p class="upload-wizard__lead">Ready to start campaign</p>
      <dl class="upload-wizard__summary">
        <dt>File</dt>
        <dd>{{ parsedFile()?.fileName }}</dd>
        <dt>Records to alert</dt>
        <dd>{{ recordsToAlert() }} ({{ skippedCount() }} skipped)</dd>
        <dt>Campaign title</dt>
        <dd>{{ settingsForm.controls.title.value }}</dd>
        <dt>Delivery</dt>
        <dd>{{ deliverySummary() }}</dd>
        <dt>Schedule</dt>
        <dd>{{ scheduleSummary() }}</dd>
      </dl>
      <div class="upload-wizard__confirm-box" role="note">
        <p class="upload-wizard__confirm-text">{{ channelAuthorizationNotice }}</p>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions class="upload-wizard__actions">
    <div class="upload-wizard__actions-start">
      @if (step() > 0) {
        <button mat-button type="button" [disabled]="starting()" (click)="goBack()">Back</button>
      }
      <button mat-button type="button" [disabled]="starting()" (click)="onCancel()">Cancel</button>
    </div>
    <div class="upload-wizard__actions-end">
      @if (step() === 0) {
        <button mat-flat-button color="primary" type="button" [disabled]="!canContinueFromUpload()" (click)="goNext()">
          Continue
        </button>
      }
      @if (step() === 1) {
        <button mat-flat-button color="primary" type="button" [disabled]="!canContinueFromMap()" (click)="goNext()">
          Continue
        </button>
      }
      @if (step() === 2) {
        <button mat-flat-button color="primary" type="button" [disabled]="!canContinueFromPreview()" (click)="goNext()">
          Continue
        </button>
      }
      @if (step() === 3) {
        <button mat-flat-button color="primary" type="button" [disabled]="!canContinueFromSettings()" (click)="goNext()">
          Continue
        </button>
      }
      @if (step() === 4) {
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="starting()"
          (click)="startCampaign()"
        >
          {{ starting() ? 'Starting…' : 'Start campaign' }}
        </button>
      }
    </div>
  </mat-dialog-actions>
</div>
